/**
 * Canonical Ertzyx product, price, and marketing-entitlement catalog.
 *
 * This module is intentionally safe to import from both server and client code: it contains
 * public product facts only. Stripe Price IDs and secret-key operations live in the server-only
 * stripe catalog resolver and are joined to these immutable specifications there.
 */

export type BillingCurrency = 'usd' | 'cad';
export type BillingInterval = 'month' | 'year' | 'one_time';
export type ProductFamily = 'individual' | 'collective' | 'institution' | 'vaas';

export type SelfServicePlanId =
  | 'verified'
  | 'legacy'
  | 'collective_verified'
  | 'collective_pro'
  | 'vaas';

export type PublicPlanId =
  | 'starter'
  | SelfServicePlanId
  | 'collective_free'
  | 'institution_pilot'
  | 'institution_core'
  | 'institution_scale'
  | 'institution_enterprise';

export type SubscriptionProductKey =
  | 'individual_verified_monthly'
  | 'individual_verified_annual'
  | 'individual_legacy_monthly'
  | 'individual_legacy_annual'
  | 'collective_verified_monthly'
  | 'collective_verified_annual'
  | 'collective_pro_monthly'
  | 'collective_pro_annual';

export type PlanProductKey = SubscriptionProductKey | 'vaas_one_time';
export type PriceCatalogKey = `${PlanProductKey}_${BillingCurrency}`;

export type PublicEntitlementKey =
  | 'public_profile'
  | 'story_ledger'
  | 'community_presence'
  | 'profile_customization'
  | 'engagement'
  | 'analytics_basic'
  | 'analytics_deep'
  | 'activity_history'
  | 'trust_signals'
  | 'verified_badge'
  | 'team_admin'
  | 'member_management'
  | 'heritage_archive'
  | 'geospatial'
  | 'bulk_tools'
  | 'api_access'
  | 'custom_branding'
  | 'promotion'
  | 'priority_support';

export type PublicPlan = {
  id: PublicPlanId;
  name: string;
  family: ProductFamily;
  description: string;
  features: readonly string[];
  entitlementKeys: readonly PublicEntitlementKey[];
  selfService: boolean;
  highlighted?: boolean;
  badge?: string;
};

export type CanonicalPriceSpec = {
  catalogKey: PriceCatalogKey;
  product: PlanProductKey;
  planId: SelfServicePlanId;
  family: Exclude<ProductFamily, 'institution'>;
  currency: BillingCurrency;
  unitAmount: number;
  recurringInterval: 'month' | 'year' | null;
  billingInterval: BillingInterval;
  requiredEnvVar: string;
  requiredProductEnvVar: string;
  requiredInProduction: true;
};

const INDIVIDUAL_FEATURES = {
  starter: [
    'Personal profile and ERV vault ID',
    'Written, photo, and video stories',
    '100 MB media vault storage',
    'Basic Life Cards and Journal',
    'Up to 3 total archive records',
    'Basic visibility controls',
    'Public profile and shareable vault link',
    'Community participation',
  ],
  verified: [
    'Everything in Starter',
    'Unlimited achievement records',
    'Voice, podcast, document, and larger photo uploads',
    'Journey Map and richer public stories',
    'Profile customization',
    'Credential evidence uploads',
    'Archive analytics and activity history',
    '5 GB media vault storage',
  ],
  legacy: [
    'Everything in Verified',
    'Extended video uploads and all supported media types',
    '20 GB media vault storage',
    'Advanced Family Ledger tools',
    'Time Capsules and Circle collaboration',
    'Advanced audience analytics',
    'All seven archive pillars',
    'Three featured story boosts each month',
    'Priority preservation support',
  ],
} as const;

const COLLECTIVE_FEATURES = {
  collective_free: [
    'Public organization profile with an INST vault ID',
    'Organization story ledger',
    'Community Explorer publishing',
    'Public stories and shareable record links',
    'Core profile and archive customization',
    'Community engagement on published stories',
    'Institution dashboard and activity overview',
    'Support request workspace',
  ],
  collective_verified: [
    'Everything in Collective Free',
    'Verification inbox and review queue',
    'Heritage archive collections and entries',
    'Institution reports and activity history',
    'Public proof pages for issued records',
    'Member and contributor management',
    'Program templates and submission intake',
  ],
  collective_pro: [
    'Everything in Collective Verified',
    'Team administration and role-based access',
    'Bulk participant import',
    'Filterable institution reports with JSON export',
    'Partnership and pilot workspaces',
    'Procurement readiness workflows and document vault',
    'Public trust pages and proof sharing',
  ],
} as const;

export const PUBLIC_PLANS: Record<PublicPlanId, PublicPlan> = {
  starter: {
    id: 'starter',
    name: 'Starter',
    family: 'individual',
    description:
      'Start your archive with the essential tools for preserving stories and milestones.',
    features: INDIVIDUAL_FEATURES.starter,
    entitlementKeys: ['public_profile', 'story_ledger', 'community_presence'],
    selfService: true,
  },
  verified: {
    id: 'verified',
    name: 'Verified',
    family: 'individual',
    description: 'Build a richer living story with more media, customization, and ways to share.',
    features: INDIVIDUAL_FEATURES.verified,
    entitlementKeys: [
      'public_profile',
      'story_ledger',
      'community_presence',
      'profile_customization',
      'analytics_basic',
      'activity_history',
      'trust_signals',
      'verified_badge',
      'promotion',
    ],
    selfService: true,
    highlighted: true,
    badge: 'Most Popular',
  },
  legacy: {
    id: 'legacy',
    name: 'Legacy',
    family: 'individual',
    description:
      'Preserve deeper personal and family history with expanded long-term archive tools.',
    features: INDIVIDUAL_FEATURES.legacy,
    entitlementKeys: [
      'public_profile',
      'story_ledger',
      'community_presence',
      'profile_customization',
      'analytics_basic',
      'analytics_deep',
      'activity_history',
      'trust_signals',
      'verified_badge',
      'promotion',
      'priority_support',
    ],
    selfService: true,
  },
  collective_free: {
    id: 'collective_free',
    name: 'Collective Free',
    family: 'collective',
    description: 'Establish your organization on the ledger and begin telling its story.',
    features: COLLECTIVE_FEATURES.collective_free,
    entitlementKeys: [
      'public_profile',
      'story_ledger',
      'community_presence',
      'profile_customization',
      'engagement',
    ],
    selfService: true,
  },
  collective_verified: {
    id: 'collective_verified',
    name: 'Collective Verified',
    family: 'collective',
    description: 'Run verification, heritage archive, reporting, and member workflows at scale.',
    features: COLLECTIVE_FEATURES.collective_verified,
    entitlementKeys: [
      'public_profile',
      'story_ledger',
      'community_presence',
      'profile_customization',
      'engagement',
      'analytics_basic',
      'activity_history',
      'trust_signals',
      'member_management',
      'heritage_archive',
    ],
    selfService: true,
    highlighted: true,
    badge: 'Most Popular',
  },
  collective_pro: {
    id: 'collective_pro',
    name: 'Collective Pro',
    family: 'collective',
    description:
      'Run higher-volume verification and organization operations with team and reporting tools.',
    features: COLLECTIVE_FEATURES.collective_pro,
    entitlementKeys: [
      'public_profile',
      'story_ledger',
      'community_presence',
      'profile_customization',
      'engagement',
      'analytics_basic',
      'activity_history',
      'trust_signals',
      'team_admin',
      'member_management',
      'heritage_archive',
      'bulk_tools',
    ],
    selfService: true,
  },
  institution_pilot: {
    id: 'institution_pilot',
    name: 'Founding Institution Pilot',
    family: 'institution',
    description:
      'Launch a scoped 90-day pilot with a defined issued-record allowance and guided setup.',
    features: [
      'Guided 90-day pilot scope',
      'Institution dashboard access',
      'Issued-record allowance set with your team',
      'Public proof pages',
      'Email support',
      'Setup planning with Ertzyx',
    ],
    entitlementKeys: [],
    selfService: false,
  },
  institution_core: {
    id: 'institution_core',
    name: 'Institution Core',
    family: 'institution',
    description: 'Issue and manage trusted records year-round with a scoped annual allowance.',
    features: [
      'Annual issued-record allowance',
      'Institution dashboard',
      'Heritage archive',
      'Member ledger',
      'Public proof pages',
      'Email support',
    ],
    entitlementKeys: [],
    selfService: false,
  },
  institution_scale: {
    id: 'institution_scale',
    name: 'Institution Scale',
    family: 'institution',
    description:
      'Scale verification and reporting across teams and programs with a larger allowance.',
    features: [
      'Everything in Institution Core',
      'Larger annual issued-record allowance',
      'Expanded heritage archive',
      'API access',
      'Bulk verification',
      'Custom branding',
      'Priority support',
    ],
    entitlementKeys: [],
    selfService: false,
  },
  institution_enterprise: {
    id: 'institution_enterprise',
    name: 'Enterprise and Public Sector',
    family: 'institution',
    description:
      'A scoped partnership for complex governance, reporting, and integration requirements.',
    features: [
      'Everything in Institution Scale',
      'Custom annual issued-record allowance',
      'Governance and reporting review',
      'Dedicated implementation planning',
      'Custom integrations',
      'Dedicated support',
    ],
    entitlementKeys: [],
    selfService: false,
  },
  vaas: {
    id: 'vaas',
    name: 'Verification-as-a-Service',
    family: 'vaas',
    description:
      'One verification credit for a single eligible achievement. No subscription required.',
    features: ['One verification credit', 'One-time payment', 'No subscription required'],
    entitlementKeys: ['trust_signals'],
    selfService: true,
  },
};

export const INSTITUTION_STARTING_PRICES = {
  institution_pilot: {
    amount: 250000,
    currency: 'usd' as const,
    label: 'From $2,500',
    period: 'for a 90-day pilot',
  },
  institution_core: {
    amount: 360000,
    currency: 'usd' as const,
    label: 'From $3,600',
    period: 'per year',
  },
  institution_scale: {
    amount: 840000,
    currency: 'usd' as const,
    label: 'From $8,400',
    period: 'per year',
  },
  institution_enterprise: {
    amount: 1500000,
    currency: 'usd' as const,
    label: 'Custom',
    period: 'generally from $15,000/year',
  },
} as const;

const PLAN_PRICE_AMOUNTS: Record<
  SelfServicePlanId,
  Record<BillingCurrency, { month?: number; year?: number; one_time?: number }>
> = {
  verified: {
    usd: { month: 999, year: 9590 },
    cad: { month: 1399, year: 13430 },
  },
  legacy: {
    usd: { month: 1699, year: 16310 },
    cad: { month: 2399, year: 23030 },
  },
  collective_verified: {
    usd: { month: 2900, year: 27840 },
    cad: { month: 3900, year: 37440 },
  },
  collective_pro: {
    usd: { month: 7900, year: 75840 },
    cad: { month: 10900, year: 104640 },
  },
  vaas: {
    usd: { one_time: 2900 },
    cad: { one_time: 3900 },
  },
};

const PRODUCT_DEFINITIONS: ReadonlyArray<{
  product: PlanProductKey;
  planId: SelfServicePlanId;
  family: Exclude<ProductFamily, 'institution'>;
  billingInterval: BillingInterval;
  recurringInterval: 'month' | 'year' | null;
}> = [
  {
    product: 'individual_verified_monthly',
    planId: 'verified',
    family: 'individual',
    billingInterval: 'month',
    recurringInterval: 'month',
  },
  {
    product: 'individual_verified_annual',
    planId: 'verified',
    family: 'individual',
    billingInterval: 'year',
    recurringInterval: 'year',
  },
  {
    product: 'individual_legacy_monthly',
    planId: 'legacy',
    family: 'individual',
    billingInterval: 'month',
    recurringInterval: 'month',
  },
  {
    product: 'individual_legacy_annual',
    planId: 'legacy',
    family: 'individual',
    billingInterval: 'year',
    recurringInterval: 'year',
  },
  {
    product: 'collective_verified_monthly',
    planId: 'collective_verified',
    family: 'collective',
    billingInterval: 'month',
    recurringInterval: 'month',
  },
  {
    product: 'collective_verified_annual',
    planId: 'collective_verified',
    family: 'collective',
    billingInterval: 'year',
    recurringInterval: 'year',
  },
  {
    product: 'collective_pro_monthly',
    planId: 'collective_pro',
    family: 'collective',
    billingInterval: 'month',
    recurringInterval: 'month',
  },
  {
    product: 'collective_pro_annual',
    planId: 'collective_pro',
    family: 'collective',
    billingInterval: 'year',
    recurringInterval: 'year',
  },
  {
    product: 'vaas_one_time',
    planId: 'vaas',
    family: 'vaas',
    billingInterval: 'one_time',
    recurringInterval: null,
  },
] as const;

function productEnvKey(planId: SelfServicePlanId): string {
  const normalized = planId === 'verified' || planId === 'legacy' ? `INDIVIDUAL_${planId}` : planId;
  return `STRIPE_PRODUCT_${normalized.toUpperCase()}`;
}

export function requiredPriceEnvVar(product: PlanProductKey, currency: BillingCurrency): string {
  return `STRIPE_PRICE_${product.toUpperCase()}_${currency.toUpperCase()}`;
}

export const SELF_SERVICE_PRICE_SPECS: readonly CanonicalPriceSpec[] = PRODUCT_DEFINITIONS.flatMap(
  (definition) =>
    (['usd', 'cad'] as const).map((currency) => {
      const unitAmount =
        PLAN_PRICE_AMOUNTS[definition.planId][currency][definition.billingInterval];
      if (unitAmount == null)
        throw new Error(`Missing canonical amount for ${definition.product}_${currency}`);
      return {
        catalogKey: `${definition.product}_${currency}` as PriceCatalogKey,
        ...definition,
        currency,
        unitAmount,
        requiredEnvVar: requiredPriceEnvVar(definition.product, currency),
        requiredProductEnvVar: productEnvKey(definition.planId),
        requiredInProduction: true as const,
      };
    })
);

export const PLAN_PRODUCTS = PRODUCT_DEFINITIONS.map((definition) => definition.product);
export const ALL_CATALOG_KEYS = SELF_SERVICE_PRICE_SPECS.map((spec) => spec.catalogKey);

export function getCanonicalPrice(
  planId: SelfServicePlanId,
  currency: BillingCurrency,
  interval: BillingInterval
): number | null {
  return PLAN_PRICE_AMOUNTS[planId]?.[currency]?.[interval] ?? null;
}

export function formatCanonicalAmount(unitAmount: number, currency: BillingCurrency): string {
  const amount = new Intl.NumberFormat('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(unitAmount / 100);
  return `${currency === 'cad' ? 'C$' : '$'}${amount}`;
}

export function formatCanonicalPlanPrice(
  planId: SelfServicePlanId,
  currency: BillingCurrency,
  interval: BillingInterval
): string {
  const amount = getCanonicalPrice(planId, currency, interval);
  return amount == null
    ? currency === 'cad'
      ? 'C$—'
      : '$—'
    : formatCanonicalAmount(amount, currency);
}

export function annualSavingsPercent(
  planId: Exclude<SelfServicePlanId, 'vaas'>,
  currency: BillingCurrency
): number {
  const monthly = getCanonicalPrice(planId, currency, 'month');
  const annual = getCanonicalPrice(planId, currency, 'year');
  if (!monthly || !annual) return 0;
  return Math.round((1 - annual / (monthly * 12)) * 100);
}

export function productKeyForSelection(
  planId: Exclude<SelfServicePlanId, 'vaas'>,
  interval: 'month' | 'year'
): SubscriptionProductKey {
  const suffix = interval === 'year' ? 'annual' : 'monthly';
  const prefix = planId === 'verified' || planId === 'legacy' ? `individual_${planId}` : planId;
  return `${prefix}_${suffix}` as SubscriptionProductKey;
}

export const COLLECTIVE_ENTITLEMENT_KEYS = {
  collective_free: PUBLIC_PLANS.collective_free.entitlementKeys,
  collective_verified: PUBLIC_PLANS.collective_verified.entitlementKeys,
  collective_pro: PUBLIC_PLANS.collective_pro.entitlementKeys,
} as const;
